import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@components/ui/sheet';
import { Separator } from '@components/ui/separator';
import { cn } from '@components/lib/utils';
import OrgMenu from '@components/AccountMenu/OrgMenu';
import AccountPopover from '@components/AccountMenu/AccountPopover';
import type { Organization } from '@app-types/index';
import type { PendingOrganizationInvitation } from '@hooks/useOrganizationInvitations';

import {
  type NavConfig,
  type NavSection,
  isSectionActive,
  moreItems,
} from './navConfig';
import type { NavBadge } from './SidebarSection';

export interface MobileNavProps {
  nav: NavConfig;
  badges: Partial<Record<string, NavBadge>>;
  hidden?: boolean;
  org: {
    organizations?: Organization[];
    selectedOrganization?: Organization;
    onCreateOrganization: () => void;
    invitations: PendingOrganizationInvitation[];
    busyInvitationId: string | null;
    acceptInvitation: (id: string) => Promise<Organization | null>;
    declineInvitation: (id: string) => Promise<boolean>;
  };
  profile?: React.ComponentProps<typeof AccountPopover>['profile'];
}

const barItemClass = (active: boolean): string =>
  cn(
    'relative flex min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 text-[11px] font-medium outline-none [-webkit-tap-highlight-color:transparent] focus-visible:ring-2 focus-visible:ring-marble-blue-500',
    active ? 'text-marble-blue-700' : 'text-marble-bg-500',
  );

const BarDot: React.FC = () => (
  <span className="absolute top-1 right-[calc(50%-14px)] size-2 rounded-full border-[1.5px] border-card bg-marble-red-500" />
);

/**
 * Under 1280px the rail becomes a bottom bar: four destinations and "More",
 * a sheet with everything else (AI Chat, Profile, Notifications, the property
 * switcher and the account). The sheet is a Radix dialog, so focus is trapped
 * while it's open and returns to "More" when it closes; any navigation closes
 * it.
 */
const MobileNav: React.FC<MobileNavProps> = ({
  nav,
  badges,
  hidden,
  org,
  profile,
}) => {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname, location.search]);

  const more = moreItems(nav);
  const moreActive = more.some((i) => isSectionActive(i, location));
  const moreHasBadge =
    more.some((i) => (badges[i.id]?.count ?? 0) > 0) ||
    org.invitations.length > 0;

  const renderBarItem = (item: NavSection) => {
    const active = isSectionActive(item, location);
    const Icon = item.icon;
    const badge = badges[item.id];
    return (
      <Link
        key={item.id}
        to={item.to}
        aria-current={active ? 'page' : undefined}
        className={barItemClass(active)}
      >
        {Icon && <Icon className="size-6" aria-hidden />}
        <span className="max-w-full truncate">{item.label}</span>
        {!!badge?.count && <BarDot />}
      </Link>
    );
  };

  return (
    <>
      <nav
        aria-label="Main"
        data-testid="mobile-nav"
        className={cn(
          'sidebar-container fixed inset-x-0 bottom-0 z-[2] flex items-stretch gap-1 rounded-t-3xl border border-marble-bg-100 bg-card px-2 pt-1.5 shadow-[0px_-4px_8px_rgba(0,0,0,0.07)] min-[1280px]:hidden',
          'pb-[calc(6px+env(safe-area-inset-bottom))]',
          hidden && 'hidden',
        )}
      >
        {nav.mobileBar.map(renderBarItem)}
        <button
          ref={moreRef}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
          className={barItemClass(moreActive || moreOpen)}
        >
          <MoreHorizontal className="size-6" aria-hidden />
          <span>More</span>
          {moreHasBadge && <BarDot />}
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          size="full"
          // "More" lives outside the sheet root, so Radix has no trigger to
          // hand focus back to; do it ourselves.
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            moreRef.current?.focus();
          }}
          className="max-h-[85vh] overflow-y-auto rounded-t-3xl p-4 pb-[calc(16px+env(safe-area-inset-bottom))]"
        >
          <SheetTitle className="text-base">More</SheetTitle>
          <SheetDescription className="sr-only">
            Other pages, your properties and your account
          </SheetDescription>

          <ul className="mt-3 flex flex-col gap-0.5">
            {more.map((item) => {
              const active = isSectionActive(item, location);
              const Icon = item.icon;
              const badge = badges[item.id];
              return (
                <li key={item.id}>
                  <Link
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-marble-blue-500',
                      active
                        ? 'bg-marble-blue-50 text-marble-blue-700 dark:bg-marble-bg-100'
                        : 'text-foreground hover:bg-marble-bg-50',
                    )}
                  >
                    {Icon && <Icon className="size-5" aria-hidden />}
                    {item.label}
                    {!!badge?.count && (
                      <span className="ml-auto rounded-full bg-marble-red-500 px-1.5 py-px text-[11px] font-semibold text-white">
                        {badge.count > 99 ? '99+' : badge.count}
                        <span className="sr-only"> {badge.label}</span>
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          <Separator className="my-3" />
          <section aria-label="Properties" className="flex flex-col gap-0.5">
            <OrgMenu
              organizations={org.organizations}
              selectedOrganization={org.selectedOrganization}
              onCreateOrganization={org.onCreateOrganization}
              onClose={() => setMoreOpen(false)}
              invitations={org.invitations}
              busyInvitationId={org.busyInvitationId}
              onAcceptInvitation={org.acceptInvitation}
              onDeclineInvitation={org.declineInvitation}
            />
          </section>

          <Separator className="my-3" />
          <section aria-label="Account" className="flex flex-col gap-0.5">
            <AccountPopover
              profile={profile}
              onClose={() => setMoreOpen(false)}
            />
          </section>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileNav;
